/**
 * Gringotts wallet transaction contract (ENG-27365).
 *
 * These describe Gringotts' own responses, read directly through the gateway
 * (`/gringotts/api/v1/wallets/...`) rather than proxied by VIP — VIP's wallet
 * transactions endpoint is deferred (ADR 0023 §15).
 *
 * Verified against beta on 2026-09-18 with wallet 2527 (facility 28, Inch Factory).
 */

/** Direction of a transaction. The Gringotts search requires one or the other. */
export type TransferType = "credit" | "debit";

/**
 * A context attribute VIP stamps on every post so a row can be described in
 * plain language. Verified against beta: the fields are `attributeName` /
 * `attributeValue`, alongside audit columns and a `deleted` flag.
 */
export interface TransactionContext {
  attributeName?: string;
  attributeValue?: string;
  deleted?: number;
  valueType?: string;
}

export interface TransactionsModel {
  id: number;
  /** VIP's reference, e.g. "VIP-INV-1234". Prefix identifies the event. */
  referenceId: string;
  /**
   * Gringotts' own movement vocabulary (e.g. "WALLET_TO_OUTSIDE"), NOT the
   * `credit`/`debit` the search filters by. Never compare it against
   * TransferType — use `LedgerRow.direction`, which records which query the
   * row came back from.
   */
  transferType: string;
  status: string;
  sourceAmount: number;
  /** Epoch milliseconds, not an ISO string. The business date — what the ledger displays. */
  transactionDate: number;
  /**
   * Epoch milliseconds. Stamped in the same save that applies the row to the
   * wallet balance, so it is the order balances were applied in — the ledger
   * sorts on it (ENG-27798). Optional so a row without it still sorts.
   */
  updatedAt?: number;
  /** Wallet balance after the movement, on the debited side. */
  sourceWalletBalance?: number;
  /** Wallet balance after the movement, on the credited side. */
  destinationWalletBalance?: number;
  source?: TransactionParty;
  destination?: TransactionParty;
  transactionContexts?: TransactionContext[];
  payMethod?: { name?: string };
  clientName?: string;
}

export interface TransactionParty {
  entity?: { name?: string };
  holdingWallet?: { id?: number };
}

/**
 * Search envelope. The payload of interest is always at index 0 — the endpoint
 * is shaped for multiple wallets but is queried one wallet at a time.
 */
export interface WalletTransactionsDTO {
  walletId: number;
  /** Total matching rows, not the page size. */
  totalCount: number;
  transactionsModelList: TransactionsModel[];
}

export interface WalletTransactionsSearchResponse {
  walletTransactionsDTOList?: WalletTransactionsDTO[];
}

/**
 * Gringotts summary figures, all for the requested window. `balance` is
 * credit − debit INSIDE the window — not the wallet balance: a row dated
 * outside it (eight legacy VIP-ADVADJ rows carry 0001-01-01) is left out
 * (ENG-28276). Read the wallet balance from VIP's wallet read instead.
 */
export interface WalletSummaryDTO {
  walletId?: number;
  balance?: number;
  totalCredit?: number;
  totalDebit?: number;
  transactionDate?: string | null;
  effectiveMinBalance?: number;
}

/**
 * Summary envelope — same shape as the transactions search: the payload is in
 * `walletDTOList[0]`, since the endpoint is built for several wallets but is
 * queried one at a time.
 */
export interface WalletSummaryResponse {
  walletDTOList?: WalletSummaryDTO[];
}

export interface WalletTransactionsParams {
  transferType: TransferType;
  pageNumber?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
}

export interface WalletDateRange {
  startDate?: string;
  endDate?: string;
}

// ---------------------------------------------------------------------------
// Open advance (VIP, ENG-27279 / ENG-27357)
// ---------------------------------------------------------------------------

/**
 * Entry states, mirroring VIP's `wallet_entries.state`
 * (vendor_invoice_portal_models/open_advance_models.go).
 */
export const WALLET_ENTRY_STATE = {
  OPEN: "OPEN",
  CONSUMED: "CONSUMED",
  EXPIRED: "EXPIRED",
  REFUNDED: "REFUNDED",
} as const;

export type WalletEntryState =
  (typeof WALLET_ENTRY_STATE)[keyof typeof WALLET_ENTRY_STATE];

/**
 * One invoice's draw on an entry. Lines are append-only: a released claim
 * (request rejected) returns its lines to the pool and stamps `released_at`, so
 * a released line is history, not a live deduction.
 */
export interface WalletAdjustment {
  id: number;
  entry_id: number;
  claim_id: number;
  invoice_id: number;
  /** Null when the invoice row is gone; fall back to the PO. */
  invoice_number?: string | null;
  /** Null when the claim covered the invoice in full (no request was raised). */
  payment_request_id?: number | null;
  amount: number;
  created_at: string;
  released_at?: string | null;
}

/**
 * What created an entry (`source_type`). Only the first has a credit note
 * behind it; the other two hold an overpayment when the PO is revised down
 * with no credit note, or cancelled (ENG-28251).
 */
export const WALLET_ENTRY_SOURCE = {
  CREDIT_NOTE_NO_UTR: "CREDIT_NOTE_NO_UTR",
  PO_REVISED_NO_CN: "PO_REVISED_NO_CN",
  PO_CANCELLED: "PO_CANCELLED",
} as const;

/**
 * A parked credit (ENG-27316), as `GET /payment-processing/wallet/entries`
 * returns it (vip#337, `WalletEntryListItem`). `amount` never changes; `balance`
 * is what is left to consume, so `amount - balance` is the consumed total.
 */
export interface WalletEntry {
  id: number;
  vendor_facility_id: number;
  /** Best-effort VMS lookup — "" when it failed; fall back to the facility id. */
  vendor_facility_name?: string;
  source_type: string;
  source_credit_note_id?: number | null;
  credit_note_number?: string | null;
  source_po_code?: string | null;
  amount: number;
  balance: number;
  state: WalletEntryState | string;
  created_at: string;
  /** Stamped at creation and never moved — the only source for ageing. */
  expires_at: string;
  nudged_at?: string | null;
  expired_at?: string | null;
  refunded_at?: string | null;
  refund_utr?: string | null;
  /** What came back by transfer; `balance` is zeroed by the refund. */
  refunded_amount?: number | null;
  /** Never null on the list response; absent on the refund response. */
  adjustments?: WalletAdjustment[];
}

/**
 * One facility's open-advance totals (ENG-27744 / ENG-27892). The entries list
 * returns them only when asked (`include=facilities`); they don't depend on the
 * page.
 */
export interface WalletFacilityTotals {
  vendor_facility_id: number;
  vendor_facility_name?: string;
  advance_credited?: number;
  advance_unadjusted?: number;
  open_advance_balance?: number;
  /** Open advance set off against invoices this IST calendar month, to date. */
  netted_this_month?: number;
}

/** Paged envelope of the entries list. `count` is this page's length, not a total. */
export interface WalletEntriesResponse {
  entries?: WalletEntry[] | null;
  count?: number;
  limit?: number;
  offset?: number;
  facilities?: WalletFacilityTotals[] | null;
}

/**
 * What the Wallet tab renders from. `enabled: false` means open advance isn't
 * available in this environment (VIP answered 503 or 404) — "this feature isn't
 * here", not an error.
 */
export interface WalletEntriesResult {
  enabled: boolean;
  entries: WalletEntry[];
  /** Per-facility totals, when they were asked for and this VIP returns them. */
  facilities?: WalletFacilityTotals[];
}

/**
 * A transaction tagged with the direction it was fetched under.
 *
 * The row's own `transferType` is Gringotts' internal vocabulary, so direction
 * is taken from the query that returned it — we asked for credits or debits
 * explicitly, which is both simpler and immune to that vocabulary changing.
 */
export type LedgerRow = TransactionsModel & { direction: TransferType };
